import * as React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Rating,
  TextField,
  Typography,
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useGridApiContext } from '@mui/x-data-grid'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import PublicEventActions from './Actions'

const DateTimeEditCell = params => {
  const { id, field, value } = params
  const apiRef = useGridApiContext()
  const [open, setOpen] = React.useState(true)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value ? new Date(value) : null}
        inputFormat="dd/MM/yyyy HH:mm"
        onChange={newValue =>
          apiRef.current.setEditCellValue({ id, field, value: newValue })
        }
        renderInput={inputParams => (
          <TextField
            {...inputParams}
            fullWidth
            variant="standard"
            onKeyDown={e => e.stopPropagation()}
            inputProps={{ ...inputParams.inputProps, readOnly: true }}
            InputProps={{ endAdornment: null }}
            onClick={() => setOpen(true)}
          />
        )}
      />
    </LocalizationProvider>
  )
}

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const getSafeCount = value => {
  const parsed = toNumber(value)
  return parsed === null ? 0 : Math.max(0, Math.trunc(parsed))
}

const getAverageRating = value => {
  const parsed = toNumber(value)
  if (parsed === null) return 0
  return Math.max(0, Math.min(5, parsed))
}

const formatDateLabel = value => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('es-EC')
}

const EventDetailCell = ({ row, feedbackState, onRetryFeedback }) => {
  const [showAllComments, setShowAllComments] = React.useState(false)

  React.useEffect(() => {
    setShowAllComments(false)
  }, [row.__parentId])

  const status = feedbackState?.status || 'idle'
  const feedback = Array.isArray(feedbackState?.feedback) ? feedbackState.feedback : []
  const averageRating = getAverageRating(feedbackState?.averageRating)
  const accessCount = getSafeCount(row.accessCount)
  const treeCount = getSafeCount(row.treeCount)

  const comments = feedback
    .filter(item => typeof item?.comentario === 'string' && item.comentario.trim() !== '')
    .map(item => ({
      id: item.id,
      comentario: item.comentario,
      createdAt: item.createdAt,
    }))

  const commentsVisible = showAllComments ? comments : comments.slice(0, 3)

  const feedbackCountFromRow = toNumber(row.feedbackCount)
  const totalFeedback =
    feedbackCountFromRow === null ? feedback.length : feedbackCountFromRow

  return (
    <Box sx={{ width: '100%', py: 1.5, pr: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.2,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Valoracion:
          </Typography>
          <Rating value={averageRating} precision={0.5} readOnly size="small" />
          <Typography variant="caption" color="text.secondary">
            {averageRating.toFixed(1)} / 5
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`Accesos: ${accessCount}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Arboles registrados: ${treeCount}`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Comentarios
        </Typography>
        <Chip
          label={`${getSafeCount(totalFeedback)}`}
          size="small"
          variant="filled"
          color="default"
        />
      </Box>

      {status === 'loading' || status === 'idle' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Cargando comentarios...
          </Typography>
        </Box>
      ) : null}

      {status === 'error' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Alert severity="error" sx={{ py: 0 }}>
            {feedbackState?.error || 'No fue posible cargar comentarios del evento.'}
          </Alert>
          <Box>
            <Button size="small" onClick={() => onRetryFeedback(row.__parentId)}>
              Reintentar
            </Button>
          </Box>
        </Box>
      ) : null}

      {status === 'success' ? (
        <>
          {commentsVisible.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {commentsVisible.map((comment, index) => (
                <Box
                  key={comment.id || `feedback-comment-${index}`}
                  sx={{
                    px: 1.2,
                    py: 0.8,
                    borderRadius: 1,
                    bgcolor: '#FFFFFF',
                    border: theme => `1px solid ${theme.palette.divider}`,
                  }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                    {comment.comentario}
                  </Typography>
                  {formatDateLabel(comment.createdAt) && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDateLabel(comment.createdAt)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin comentarios aun.
            </Typography>
          )}

          {comments.length > 3 && (
            <Button
              size="small"
              sx={{ mt: 0.8, p: 0, minWidth: 'auto' }}
              onClick={() => setShowAllComments(prev => !prev)}>
              {showAllComments ? 'Ver menos' : 'Ver mas'}
            </Button>
          )}
        </>
      ) : null}
    </Box>
  )
}

export const getColumns = (
  rowId,
  setRowId,
  onEdit,
  expandedRowIds,
  onToggleRow,
  feedbackByEventId,
  onRetryFeedback,
  onView,
  onRequestDelete,
) => {
  const columns = [
    {
      field: '__expand',
      headerName: '',
      headerAlign: 'center',
      align: 'center',
      width: 54,
      minWidth: 54,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      cellClassName: params =>
        params.row?.__isDetail ? 'event-detail-cell' : 'event-expand-cell',
      renderCell: params => {
        if (params.row?.__isDetail) {
          const parentId = params.row.__parentId
          const feedbackState = feedbackByEventId[parentId]
          return (
            <EventDetailCell
              row={params.row}
              feedbackState={feedbackState}
              onRetryFeedback={onRetryFeedback}
            />
          )
        }

        const isExpanded = expandedRowIds.includes(params.row.id)
        return (
          <IconButton
            size="small"
            aria-label={isExpanded ? 'Contraer detalle' : 'Expandir detalle'}
            onClick={event => {
              event.stopPropagation()
              onToggleRow(params.row.id)
            }}>
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        )
      },
    },
    {
      field: 'code',
      headerName: 'Código',
      headerAlign: 'center',
      flex: 1,
      minWidth: 130,
      editable: false,
    },
    {
      field: 'descripcion',
      headerName: 'Descripción',
      headerAlign: 'center',
      flex: 2,
      minWidth: 160,
      editable: true,
    },
    {
      field: 'user',
      headerName: 'Usuario',
      headerAlign: 'center',
      flex: 1.5,
      minWidth: 140,
      editable: false,
      valueGetter: params => {
        const u = params.row.user
        return u ? `${u.nombre} ${u.apellido}` : params.row.userId
      },
    },
    {
      field: 'tenant',
      headerName: 'Tenant',
      headerAlign: 'center',
      flex: 1,
      minWidth: 110,
      editable: false,
      valueGetter: params => params.row.tenant?.nombre || params.row.tenantId,
    },
    {
      field: 'startDate',
      headerName: 'Inicio',
      headerAlign: 'center',
      flex: 1.2,
      minWidth: 140,
      editable: false,
      valueFormatter: params =>
        params.value ? new Date(params.value).toLocaleString('es-EC') : '',
    },
    {
      field: 'endDate',
      headerName: 'Fin',
      headerAlign: 'center',
      flex: 1.2,
      minWidth: 140,
      editable: true,
      valueFormatter: params =>
        params.value ? new Date(params.value).toLocaleString('es-EC') : '',
      renderEditCell: params => <DateTimeEditCell {...params} />,
    },
    {
      field: 'isActive',
      headerName: 'Activo',
      headerAlign: 'center',
      type: 'boolean',
      flex: 0.7,
      minWidth: 80,
      editable: true,
    },
    {
      field: 'estado',
      headerName: 'Estado',
      headerAlign: 'center',
      flex: 0.9,
      minWidth: 100,
      editable: false,
      renderCell: params => {
        const now = new Date()
        const start = new Date(params.row.startDate)
        const end = new Date(params.row.endDate)
        const active = params.row.isActive

        let label = 'Inactivo'
        let color = 'default'

        if (!active) {
          label = 'Desactivado'
          color = 'error'
        } else if (now < start) {
          label = 'Pendiente'
          color = 'warning'
        } else if (now >= start && now <= end) {
          label = 'En curso'
          color = 'success'
        } else {
          label = 'Finalizado'
          color = 'default'
        }

        return <Chip label={label} color={color} size="small" />
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      type: 'actions',
      renderCell: params =>
        params.row?.__isDetail ? null : (
          <PublicEventActions {...{ params, rowId, setRowId, onEdit, onView, onRequestDelete }} />
        ),
      minWidth: 200,
      flex: 1.2,
    },
  ]

  const totalColumns = columns.length
  columns[0].colSpan = params => (params.row?.__isDetail ? totalColumns : 1)

  return columns
}
